/**
 * <cart-upsell-slider>
 *
 * Initialises a Swiper-based product upsell slider inside the cart drawer.
 * swiper-bundle.min.js is loaded globally via theme.liquid, so window.Swiper
 * is guaranteed to be available when this script runs (both use defer).
 *
 * Re-initialises after every cart update because morphSection (used by
 * cart-items-component) patches the Swiper DOM in-place without triggering
 * connectedCallback / disconnectedCallback on the custom element.
 *
 * Cart updates can dispatch multiple `cart:update` events for a single user
 * action (e.g. more than one `cart-items-component` on the page reacting to
 * the same update), and each morph can land across more than one animation
 * frame depending on layout/font work still settling. Debouncing the
 * reinit — and re-measuring on the frame *after* the DOM has settled — avoids
 * Swiper being destroyed/recreated mid-flight, which is what caused the
 * swatches/gaps to intermittently look broken until a full page reload.
 *
 * NOTE: this file is loaded as a plain classic script (no type="module" —
 * see layout/theme.liquid), so it must NOT use `import`/`export` statements.
 */

/**
 * Minimal local debounce helper (classic-script friendly — no module import).
 * @param {() => void} fn
 * @param {number} wait
 * @returns {(() => void) & { cancel(): void }}
 */
function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;
  function debounced() {
    clearTimeout(timeout);
    timeout = setTimeout(fn, wait);
  }
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
}

class CartUpsellSlider extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;
  /** @type {(() => void) | null} */
  #cartUpdateHandler = null;
  /** @type {(() => void) & { cancel(): void } | null} */
  #debouncedReinit = null;

  connectedCallback() {
    requestAnimationFrame(() => this.#init());

    this.#debouncedReinit = debounce(() => {
      // Wait a frame for the morph to finish, then one more for layout
      // (fonts/images reflow) to settle before Swiper measures slide widths.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#reinit());
      });
    }, 50);

    this.#cartUpdateHandler = () => this.#debouncedReinit?.();
    document.addEventListener('cart:update', this.#cartUpdateHandler);
  }

  disconnectedCallback() {
    this.#debouncedReinit?.cancel();
    this.#debouncedReinit = null;
    this.#destroy();
    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:update', this.#cartUpdateHandler);
      this.#cartUpdateHandler = null;
    }
  }

  #destroy() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  #reinit() {
    this.#destroy();
    this.#init();
  }

  #init() {
    const swiperEl = this.querySelector('.cart-upsell-swiper');
    const paginationEl = this.querySelector('.cart-upsell-dots');
    if (!swiperEl) return;

    // Defensive: if a previous instance is still attached to this exact
    // element (e.g. an overlapping reinit), tear it down first instead of
    // stacking a second Swiper instance on top of it.
    const existingSwiper = /** @type {any} */ (swiperEl).swiper;
    if (existingSwiper) {
      existingSwiper.destroy(true, true);
    }

    this.#swiper = new window.Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 12,
      slidesOffsetBefore: 25,
      slidesOffsetAfter: 25,
      // Auto re-measure slide sizes whenever the slider's own DOM/children
      // resize (e.g. images finishing layout, fonts swapping in) instead of
      // relying solely on the one-shot measurement taken at init time.
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      pagination: {
        el: paginationEl,
        clickable: true,
      },
    });
  }
}

customElements.define('cart-upsell-slider', CartUpsellSlider);
